import {
  webAppBatchGetPersons,
  webAppGetPersonsByIdQueryKey,
  WebAppPersonDto,
} from '@mi-front/api-v2';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PERSON_QUERY_STALE_TIME_MS } from '../person-constants';

export const personsByIdsQueryKey = (personIds: string[]) =>
  ['api-v2', 'persons', 'batch-get', personIds] as const;

/**
 * Dedupes, sorts, and batch-fetches persons by id. Warms the individual
 * `useWebAppGetPersonsById` query cache so single-person lookups are free afterwards.
 * Returns an empty map on error / missing ids so callers can fall back to voice_label.
 */
export function usePersonsByIds(personIds: string[]) {
  const queryClient = useQueryClient();

  // Stabilize when the caller passes a new array instance with the same ids.
  const uniqueIdsKey = [...new Set(personIds.filter(Boolean))].sort().join(',');
  const stableIds = useMemo(() => uniqueIdsKey.split(',').filter(Boolean), [uniqueIdsKey]);

  const { data, isLoading, isError } = useQuery({
    queryKey: personsByIdsQueryKey(stableIds),
    enabled: stableIds.length > 0,
    staleTime: PERSON_QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await webAppBatchGetPersons({ personIds: stableIds });

      // Warm individual keys and cancel any in-flight single-gets so a late
      // 404/error cannot overwrite batch data for that personId.
      result.persons.forEach((person) => {
        const queryKey = webAppGetPersonsByIdQueryKey(person.personId);
        void queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, person);
      });

      return result;
    },
  });

  const personsById = useMemo(() => {
    const map = new Map<string, WebAppPersonDto>();
    if (isError || !data?.persons) {
      return map;
    }
    data.persons.forEach((person) => {
      map.set(person.personId, person);
    });
    return map;
  }, [data, isError]);

  return {
    personsById,
    isLoading,
    isError,
  };
}
